/**
 * purchaseFlow — orchestrates a single printable purchase from click to
 * cloud-library save (web variant of studioCore's app/src/components/printables/purchaseFlow.js).
 *
 * Flow:
 *   1. Call pse-printables purchase endpoint (debits credits + records entitlement)
 *   2. On 402: return { status: 'insufficient', required, currentBalance }
 *   3. On success: hand off the R2 image URL to the Cloud Library worker
 *      (which fetches it server-side — bypasses browser CORS that blocks
 *      direct cross-origin R2 reads from studiocloud-dda.pages.dev).
 *   4. Return { status: 'saved', design, newBalance, alreadyOwned }
 *
 * The caller (Printables.jsx) is responsible for showing the success modal
 * and routing to library/print based on user choice.
 */

/**
 * Buy + save. Returns one of:
 *   { status: 'saved',        design, newBalance, alreadyOwned, printable }
 *   { status: 'insufficient', required, currentBalance }
 *   { status: 'error',        error, errorCode, errorDetail }
 *
 * Error copy is NOT built here: a client-generated failure returns an
 * `errorCode` that Printables.jsx maps to a printables:purchase.errors.* key
 * and translates at the display point (same contract as the desktop file).
 * `error` carries a server-supplied message when there is one.
 *
 * `saveDesign` is the cloud-library helper from useCloudLibrary(); it
 * accepts an `imageUrl` field that the worker fetches server-side, so we
 * never touch the R2 asset from the browser (which would fail CORS).
 */
export async function purchaseAndSave({ poster, purchasePrintable, saveDesign }) {
  const idempotencyKey = `printables_${poster.id}_${(globalThis.crypto?.randomUUID?.() ?? Date.now())}`;

  let result;
  try {
    result = await purchasePrintable(poster.id, idempotencyKey);
  } catch (err) {
    if (err.status === 402) {
      return {
        status: 'insufficient',
        required: err.data?.required ?? null,
        currentBalance: err.data?.currentBalance ?? null,
      };
    }
    return { status: 'error', error: err.message || null, errorCode: err.message ? null : 'purchaseFailed' };
  }

  const printable = result.printable;
  if (!printable) {
    return { status: 'error', error: null, errorCode: 'missingPayload' };
  }

  // Same pattern as AIGeneratorSection auto-save: pass imageUrl, no client
  // fetch. Cloud Library worker fetches the image server-side (no CORS).
  const imageUrl = printable.fullResUrl || printable.previewUrl || printable.thumbnailUrl;
  if (!imageUrl) {
    return {
      status: 'error',
      error: null,
      errorCode: 'prepareFailed',
      errorDetail: 'no image URL',
      purchased: true,
      printable,
    };
  }

  let savedDesign;
  try {
    savedDesign = await saveDesign({
      name: printable.title || 'Printable',
      source: 'printables',
      imageUrl,
      category: printable.category?.name || null,
      metadata: {
        printableId: printable.id,
        entitlementId: result.entitlementId || null,
        purchasedAt: result.purchasedAt || new Date().toISOString(),
        // Brightivity: no image links in the shared row's metadata (they would be brightivity.dev links);
        // image_url (stored form, set by the library) is what every app shows.
      },
    });
  } catch (err) {
    return {
      status: 'error',
      error: null,
      errorCode: 'saveThrew',
      errorDetail: err.message,
      purchased: true,
      printable,
    };
  }

  return {
    status: 'saved',
    design: savedDesign,
    printable,
    newBalance: result.newBalance ?? null,
    alreadyOwned: !!result.alreadyOwned,
  };
}
